import {
  ACCOUNT_KEYS,
  JOURNAL_EVENT_KINDS,
  JOURNAL_EXPORT_FORMATS,
  PAYOUT_METHODS,
  REVENUE_SHARE_BASES,
  SERVICE_ITEM_CATEGORIES,
  SHARE_EVENT_KINDS,
  SHARE_METHODS,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, isoDaySchema } from './clinical-common.ts';
import { amountSchema, signedAmountSchema } from './money.ts';

// Accounting integration and doctor revenue share
// (docs/plans/phase3-accounting-and-revenue-share.md §4). Money crosses the wire as cents in
// strings (ADR 0006). Nothing here carries a patient name or number.

// --- Chart-of-accounts mapping --------------------------------------------------------------

export const accountGroupSchema = z.enum([
  'revenue',
  'contra_revenue',
  'asset',
  'liability',
  'expense',
  'equity',
]);

export const accountMappingSchema = z.object({
  /** Null for the key as a whole; a payer for payer-scoped keys. */
  scopeId: z.uuid().nullable(),
  scopeName: z.string().nullable(),
  code: z.string().nullable(),
  name: z.string(),
  version: z.number().int(),
});

export const accountRowSchema = z.object({
  key: z.enum(ACCOUNT_KEYS),
  group: accountGroupSchema,
  defaultName: z.string(),
  description: z.string(),
  payerScoped: z.boolean(),
  /** The key's own mapping, then any per-payer mappings. */
  mappings: z.array(accountMappingSchema),
});
export type AccountRowView = z.infer<typeof accountRowSchema>;

export const accountListSchema = z.object({
  accounts: z.array(accountRowSchema),
  /** Payers, for mapping the payer receivable per payer. */
  payers: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
export type AccountListView = z.infer<typeof accountListSchema>;

export const putAccountBodySchema = z.object({
  key: z.enum(ACCOUNT_KEYS),
  scopeId: z.uuid().nullable(),
  code: z.string().trim().max(30).nullable(),
  name: z.string().trim().min(1).max(120),
  /** The version the editor saw; null for a new mapping. */
  version: z.number().int().positive().nullable(),
});
export type PutAccountBody = z.infer<typeof putAccountBodySchema>;

// --- Journals ---------------------------------------------------------------------------------

export const journalQuerySchema = z.object({ from: isoDaySchema, to: isoDaySchema });

export const journalExportQuerySchema = journalQuerySchema.extend({
  format: z.enum(JOURNAL_EXPORT_FORMATS),
});
export const periodExportQuerySchema = z.object({ format: z.enum(JOURNAL_EXPORT_FORMATS) });

export const journalLineSchema = z.object({
  key: z.enum(ACCOUNT_KEYS),
  code: z.string().nullable(),
  name: z.string(),
  counterparty: z.string().nullable(),
  debit: amountSchema,
  credit: amountSchema,
});

export const journalEntrySchema = z.object({
  entryNo: z.number().int(),
  journalNumber: z.string(),
  postedOn: isoDaySchema,
  kind: z.enum(JOURNAL_EVENT_KINDS),
  description: z.string(),
  late: z.boolean(),
  eventCount: z.number().int(),
  lines: z.array(journalLineSchema),
});
export type JournalEntryView = z.infer<typeof journalEntrySchema>;

export const reconciliationRowSchema = z.object({
  /** What is compared, in words: "Revenue: Laboratory", "Received by M-Pesa". */
  label: z.string(),
  /** The Wodi report the figure comes from. */
  report: z.enum(['revenue', 'collections']),
  journal: signedAmountSchema,
  reported: signedAmountSchema,
  matches: z.boolean(),
});

export const trialBalanceRowSchema = z.object({
  key: z.enum(ACCOUNT_KEYS),
  code: z.string().nullable(),
  name: z.string(),
  debit: amountSchema,
  credit: amountSchema,
});

export const journalViewSchema = z.object({
  facility: z.object({ id: z.uuid(), code: z.string(), name: z.string() }),
  from: isoDaySchema,
  to: isoDaySchema,
  /** `open` for a live journal of open days; `closed` for a closed period's frozen one. */
  status: z.enum(['open', 'closed']),
  periodId: z.uuid().nullable(),
  totals: z.object({
    debit: amountSchema,
    credit: amountSchema,
    balanced: z.boolean(),
    entryCount: z.number().int(),
    lineCount: z.number().int(),
    lateLineCount: z.number().int(),
  }),
  entries: z.array(journalEntrySchema),
  trialBalance: z.array(trialBalanceRowSchema),
  /** Open journals only: the journal against Wodi's revenue and collections reports. */
  reconciliation: z.array(reconciliationRowSchema),
  /** Stock movements of batches no GRN priced (plan A4): listed, never valued. */
  unvaluedStock: z.object({ movementCount: z.number().int(), itemCount: z.number().int() }),
  /** Charges in the range that have no price yet, so no revenue line. */
  unpricedChargeCount: z.number().int(),
});
export type JournalView = z.infer<typeof journalViewSchema>;

// --- Periods ----------------------------------------------------------------------------------

export const accountingPeriodSchema = z.object({
  id: z.uuid(),
  startsOn: isoDaySchema,
  endsOn: isoDaySchema,
  closedAt: z.iso.datetime(),
  closedBy: actorSchema,
  debit: amountSchema,
  credit: amountSchema,
  entryCount: z.number().int(),
  lineCount: z.number().int(),
  lateLineCount: z.number().int(),
});
export type AccountingPeriodView = z.infer<typeof accountingPeriodSchema>;

export const accountingPeriodListSchema = z.object({
  periods: z.array(accountingPeriodSchema),
  /** The first day the next period must start on; null before any period is closed. */
  nextStartsOn: isoDaySchema.nullable(),
  /** The latest day a period may end on: yesterday, East Africa Time. */
  latestEndsOn: isoDaySchema,
});
export type AccountingPeriodListView = z.infer<typeof accountingPeriodListSchema>;

export const closePeriodBodySchema = z.object({
  /** Required for the first period; must be `nextStartsOn` afterwards. */
  startsOn: isoDaySchema,
  endsOn: isoDaySchema,
});
export type ClosePeriodBody = z.infer<typeof closePeriodBodySchema>;

// --- Doctor revenue share ---------------------------------------------------------------------

export const revenueShareSettingsSchema = z.object({
  current: z.enum(REVENUE_SHARE_BASES),
  /** Changes in date order; empty means billed from the start (R1). */
  changes: z.array(
    z.object({
      basis: z.enum(REVENUE_SHARE_BASES),
      effectiveFrom: isoDaySchema,
      changedBy: actorSchema,
    }),
  ),
});
export type RevenueShareSettingsView = z.infer<typeof revenueShareSettingsSchema>;

export const putRevenueShareSettingsBodySchema = z.object({
  basis: z.enum(REVENUE_SHARE_BASES),
  effectiveFrom: isoDaySchema,
});

export const revenueSharePractitionerSchema = z.object({
  userId: z.uuid(),
  displayName: z.string(),
  cadre: z.string().nullable(),
});
export type RevenueSharePractitionerView = z.infer<typeof revenueSharePractitionerSchema>;

export const revenueShareRuleSchema = z.object({
  id: z.uuid(),
  practitioner: actorSchema,
  serviceItem: z.object({ id: z.uuid(), code: z.string(), name: z.string() }).nullable(),
  category: z.enum(SERVICE_ITEM_CATEGORIES).nullable(),
  method: z.enum(SHARE_METHODS),
  /** Percent rules, in basis points (12.5% is 1250). */
  rateBasisPoints: z.number().int().nullable(),
  /** Fixed rules, per unit. */
  fixedAmount: amountSchema.nullable(),
  effectiveFrom: isoDaySchema,
  effectiveTo: isoDaySchema.nullable(),
  endReason: z.string().nullable(),
});
export type RevenueShareRuleView = z.infer<typeof revenueShareRuleSchema>;

export const revenueShareRuleQuerySchema = z.object({
  practitionerUserId: z.uuid().optional(),
});

export const createRevenueShareRuleBodySchema = z
  .object({
    practitionerUserId: z.uuid(),
    serviceItemId: z.uuid().nullable(),
    category: z.enum(SERVICE_ITEM_CATEGORIES).nullable(),
    method: z.enum(SHARE_METHODS),
    rateBasisPoints: z.number().int().min(1).max(10_000).nullable(),
    fixedAmount: amountSchema.nullable(),
    effectiveFrom: isoDaySchema,
  })
  .refine((b) => (b.serviceItemId === null) !== (b.category === null), {
    message: 'Choose either one service or one category',
    path: ['category'],
  })
  .refine(
    (b) =>
      b.method === 'percent'
        ? b.rateBasisPoints !== null && b.fixedAmount === null
        : b.fixedAmount !== null && b.fixedAmount.cents !== '0' && b.rateBasisPoints === null,
    {
      message: 'A percent rule has a rate; a fixed rule has an amount above zero',
      path: ['method'],
    },
  );
export type CreateRevenueShareRuleBody = z.infer<typeof createRevenueShareRuleBodySchema>;

export const endRevenueShareRuleBodySchema = z.object({
  /** The first day the rule no longer applies. */
  endsFrom: isoDaySchema,
  reason: z.string().trim().min(1).max(500),
});

export const REVENUE_SHARE_STATEMENT_STATUSES = [
  'awaiting_approval',
  'approved',
  'rejected',
  'withdrawn',
  'paid',
] as const;
export type RevenueShareStatementStatus = (typeof REVENUE_SHARE_STATEMENT_STATUSES)[number];

export const revenueShareStatementSummarySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  practitioner: actorSchema,
  periodFrom: isoDaySchema,
  periodTo: isoDaySchema,
  earned: signedAmountSchema,
  adjustments: signedAmountSchema,
  payable: signedAmountSchema,
  paid: amountSchema,
  status: z.enum(REVENUE_SHARE_STATEMENT_STATUSES),
  preparedAt: z.iso.datetime(),
});
export type RevenueShareStatementSummaryView = z.infer<typeof revenueShareStatementSummarySchema>;

export const revenueShareStatementLineSchema = z.object({
  lineNo: z.number().int(),
  kind: z.enum(SHARE_EVENT_KINDS),
  eventOn: isoDaySchema,
  serviceDay: isoDaySchema,
  itemCode: z.string(),
  itemName: z.string(),
  category: z.enum(SERVICE_ITEM_CATEGORIES),
  chargeTotal: amountSchema,
  base: signedAmountSchema,
  share: signedAmountSchema,
  /** "30%" or "KES 500 per unit". */
  rule: z.string(),
});
export type RevenueShareStatementLineView = z.infer<typeof revenueShareStatementLineSchema>;

export const revenueSharePayoutSchema = z.object({
  id: z.uuid(),
  method: z.enum(PAYOUT_METHODS),
  reference: z.string().nullable(),
  paidOn: isoDaySchema,
  amount: amountSchema,
  recordedBy: actorSchema,
  recordedAt: z.iso.datetime(),
});

export const revenueShareStatementSchema = revenueShareStatementSummarySchema.extend({
  preparedBy: actorSchema,
  decidedBy: actorSchema.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  decisionNote: z.string().nullable(),
  lines: z.array(revenueShareStatementLineSchema),
  payouts: z.array(revenueSharePayoutSchema),
  /** What this caller may do now, decided on the server. */
  canDecide: z.boolean(),
  canWithdraw: z.boolean(),
  canRecordPayout: z.boolean(),
});
export type RevenueShareStatementView = z.infer<typeof revenueShareStatementSchema>;

export const revenueShareStatementListQuerySchema = z.object({
  status: z.enum(REVENUE_SHARE_STATEMENT_STATUSES).optional(),
  practitionerUserId: z.uuid().optional(),
});

export const prepareStatementBodySchema = z.object({
  practitionerUserId: z.uuid(),
  periodFrom: isoDaySchema,
  periodTo: isoDaySchema,
  clientRequestId: z.uuid(),
});
export type PrepareStatementBody = z.infer<typeof prepareStatementBodySchema>;

export const statementDecisionBodySchema = z.object({
  note: z.string().trim().min(1).max(500).optional(),
});
export const statementReasonBodySchema = z.object({
  note: z.string().trim().min(1).max(500),
});

export const recordPayoutBodySchema = z
  .object({
    method: z.enum(PAYOUT_METHODS),
    reference: z.string().trim().min(1).max(64).nullable(),
    paidOn: isoDaySchema,
    amount: amountSchema,
    clientRequestId: z.uuid(),
  })
  .refine((b) => b.method === 'cash' || b.reference !== null, {
    message: 'Give the M-Pesa code, bank reference or cheque number',
    path: ['reference'],
  });
export type RecordPayoutBody = z.infer<typeof recordPayoutBodySchema>;
